import { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTheme } from '@/store/useTheme'
import type { RelationshipType } from '@/store/sessionTypes'
import { SessionTypeIcon } from '@/components/SessionTypeIcon'
import { SESSION_TYPE_OPTIONS } from '@/store/clientFormOptions'
import {
  getClientById,
  updateClient,
  SESSION_TYPE_LABELS,
  DATA_CONSENT_LABELS,
  type ClientRecord,
  type SessionLogEntry,
} from '@/store/clients'
import { WOT_LABELS } from '@/store/demoClients'
import formStyles from './AddClientPage.module.css'
import styles from './ClientRecordPage.module.css'

function formatSessionDate(isoDate: string): string {
  const date = new Date(`${isoDate}T12:00:00`)
  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

function sortedSessions(history: SessionLogEntry[]): SessionLogEntry[] {
  return [...history].sort((a, b) => b.date.localeCompare(a.date))
}

export default function ClientRecordPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { palette } = useTheme()

  const [client, setClient] = useState<ClientRecord | undefined>(() =>
    id ? getClientById(id) : undefined
  )
  const [editing, setEditing] = useState(false)
  const [notes, setNotes] = useState(() => client?.notes ?? '')

  const [editName,        setEditName]        = useState(client?.name ?? '')
  const [editSessionType, setEditSessionType] = useState<RelationshipType | null>(client?.sessionType ?? null)

  const notesInitialized = useRef(false)

  useEffect(() => {
    if (!id) return
    const found = getClientById(id)
    setClient(found)
    if (found) {
      setNotes(found.notes ?? '')
      setEditName(found.name)
      setEditSessionType(found.sessionType)
      notesInitialized.current = true
    }
  }, [id])

  // Auto-save notes
  useEffect(() => {
    if (!client || !notesInitialized.current || editing) return
    const t = setTimeout(() => {
      if (notes !== (client.notes ?? '')) {
        const updated = updateClient(client.id, { notes })
        if (updated) setClient(updated)
      }
    }, 450)
    return () => clearTimeout(t)
  }, [notes, client, editing])

  const paletteVars = {
    '--cr-bg':   palette.bg,
    '--cr-text': palette.text,
    '--cr-ring': palette.ring,
    '--ac-bg':   palette.bg,
    '--ac-text': palette.text,
    '--ac-ring': palette.ring,
  } as React.CSSProperties

  if (!client) {
    return (
      <main className={styles.page} style={paletteVars} aria-label="Client record">
        <div className={styles.inner}>
          <button type="button" className={styles.backLink} onClick={() => navigate('/clients')}>
            ← Clients
          </button>
          <p className={styles.notFound}>Client not found.</p>
        </div>
      </main>
    )
  }

  const canSaveEdit =
    editName.trim().length > 0 &&
    editSessionType !== null

  function startEdit() {
    setEditName(client.name)
    setEditSessionType(client.sessionType)
    setEditing(true)
  }

  function cancelEdit() {
    setEditName(client.name)
    setEditSessionType(client.sessionType)
    setEditing(false)
  }

  function saveEdit() {
    if (!canSaveEdit || editSessionType === null) return
    const updated = updateClient(client.id, {
      name: editName.trim(),
      sessionType: editSessionType,
      track: client.track,
    })
    if (updated) {
      setClient(updated)
      setEditing(false)
    }
  }

  return (
    <main className={styles.page} style={paletteVars} aria-label={`About ${client.name}`}>
      <div className={styles.inner}>
        <button type="button" className={styles.backLink} onClick={() => navigate('/clients')}>
          ← Clients
        </button>

        <header className={styles.header}>
          <h1 className={styles.title}>{editing ? 'Edit client' : client.name}</h1>
          {!editing && (
            <button type="button" className={styles.editBtn} onClick={startEdit}>
              Edit
            </button>
          )}
        </header>

        {editing ? (
          <div className={formStyles.form}>
            <div className={formStyles.field}>
              <label className={formStyles.label} htmlFor="edit-client-name">
                Client name
              </label>
              <input
                id="edit-client-name"
                type="text"
                className={formStyles.textInput}
                value={editName}
                onChange={e => setEditName(e.target.value)}
                autoComplete="off"
              />
            </div>

            <div className={formStyles.field}>
              <span className={formStyles.label} id="edit-session-type-label">
                Session type
              </span>
              <div className={formStyles.cardGrid} role="group" aria-labelledby="edit-session-type-label">
                {SESSION_TYPE_OPTIONS.map(opt => (
                  <button
                    key={opt.id}
                    type="button"
                    className={`${formStyles.optionCard} ${formStyles.optionCardInline}`}
                    data-selected={editSessionType === opt.id}
                    aria-pressed={editSessionType === opt.id}
                    onClick={() => setEditSessionType(opt.id)}
                  >
                    <SessionTypeIcon type={opt.id} ring={palette.ring} className={formStyles.optionIcon} />
                    <span className={formStyles.optionLabel}>{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.editActions}>
              <button
                type="button"
                className={styles.saveBtn}
                disabled={!canSaveEdit}
                onClick={saveEdit}
              >
                Save changes
              </button>
              <button type="button" className={styles.cancelBtn} onClick={cancelEdit}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className={styles.details}>
              <div className={styles.detailRow}>
                <p className={styles.detailLabel}>Session type</p>
                <p className={styles.detailValue}>{SESSION_TYPE_LABELS[client.sessionType]}</p>
              </div>
              <div className={styles.detailRow}>
                <p className={styles.detailLabel}>Data and privacy</p>
                <p className={styles.detailValue}>{DATA_CONSENT_LABELS[client.dataConsent]}</p>
              </div>
            </div>

            {client.sessionHistory && client.sessionHistory.length > 0 && (
              <section className={styles.sessionsSection} aria-label="Session history">
                <h2 className={styles.sessionsHeading}>Session history</h2>
                <ul className={styles.sessionsList}>
                  {sortedSessions(client.sessionHistory).map(session => (
                    <li key={session.id} className={styles.sessionCard}>
                      <p className={styles.sessionDate}>{formatSessionDate(session.date)}</p>

                      {session.practices && session.practices.length > 0 && (
                        <p className={styles.sessionPractices}>
                          {session.practices.join(' · ')}
                        </p>
                      )}

                      <div className={styles.metricsGrid} aria-label="Session scores">
                        <div className={styles.metricBlock}>
                          <p className={styles.metricLabel}>Window of tolerance</p>
                          <p className={styles.metricValue}>
                            {WOT_LABELS[session.wotStart]} → {WOT_LABELS[session.wotEnd]}
                          </p>
                        </div>
                        <div className={styles.metricBlock}>
                          <p className={styles.metricLabel}>Mood (PANAS)</p>
                          <p className={styles.metricValue}>
                            {session.panasStart}% → {session.panasEnd}%
                          </p>
                        </div>
                      </div>

                      <div className={styles.sessionNotesBlock}>
                        <p className={styles.sessionNotesLabel}>Practitioner notes</p>
                        <p className={styles.sessionNotesText}>{session.practitionerNotes}</p>
                      </div>

                      {session.followUpNotes && (
                        <div className={styles.sessionNotesBlock}>
                          <p className={styles.sessionNotesLabel}>Follow-up</p>
                          <p className={styles.sessionNotesText}>{session.followUpNotes}</p>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <div className={styles.notesSection}>
              <label className={styles.notesLabel} htmlFor="client-notes">
                Background notes
              </label>
              <textarea
                id="client-notes"
                className={styles.notesInput}
                placeholder="Anything useful to remember about this client…"
                value={notes}
                onChange={e => setNotes(e.target.value)}
              />
              <p className={styles.notesHint}>Saved automatically</p>
            </div>
          </>
        )}
      </div>
    </main>
  )
}
